"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, PlayCircle } from "lucide-react";
import { type SiteEvent } from "@/lib/events";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/portal/Modal";
import { EventVideo } from "@/components/sections/EventVideo";

/** Card for one event - shared by the home page teaser and the /events list. "View event" opens its image and video in a pop-up. */
export function EventCard({ event }: { event: SiteEvent }) {
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [touchX, setTouchX] = useState<number | null>(null);
  // the pop-up shows one picture or video at a time, with arrows / swipe to move between them
  const slides: { type: "image" | "video"; src: string; caption: string | null }[] = [
    ...(event.cover ? [{ type: "image" as const, src: event.cover, caption: event.coverCaption }] : []),
    ...event.gallery.map((g) => ({ type: "image" as const, src: g.image, caption: g.caption })),
    ...event.videos.map((v) => ({ type: "video" as const, src: v.src, caption: v.caption })),
  ];
  const current = slides[slide];
  const go = (n: number) => setSlide((n + slides.length) % slides.length);
  const body = (event.description ?? "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <>
      <article className="he-card he-hover group flex h-full min-w-0 flex-col overflow-hidden !p-0">
        <div className="relative aspect-[400/250]">
          {event.cover ? (
            // eslint-disable-next-line @next/next/no-img-element -- served from the API host
            <img src={event.cover} alt={event.coverCaption ?? event.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-brand-gradient" aria-hidden />
          )}
          {event.videos.length > 0 && (
            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-700">
              <PlayCircle className="h-3.5 w-3.5" /> Video
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-5 [overflow-wrap:anywhere]">
          {event.location && (
            <div className="he-soft flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="he-brand-text h-3.5 w-3.5" /> {event.location}
              </span>
            </div>
          )}
          <h3 className="mt-2 text-base font-bold leading-snug">{event.title}</h3>
          {event.summary && <p className="he-soft mt-2 line-clamp-3 text-sm leading-relaxed">{event.summary}</p>}
          <div className="mt-auto pt-4">
            <Button type="button" size="sm" withArrow onClick={() => { setSlide(0); setOpen(true); }}>
              View event
            </Button>
          </div>
        </div>
      </article>

      <Modal open={open} onClose={() => setOpen(false)} title={event.title} size="lg">
        <div className="min-w-0 space-y-5">
          {slides.length > 0 && (
            <div
              className="relative mx-auto min-w-0 max-w-[400px]"
              onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchX === null || slides.length < 2) return;
                const dx = e.changedTouches[0].clientX - touchX;
                if (Math.abs(dx) > 50) go(slide + (dx < 0 ? 1 : -1));
                setTouchX(null);
              }}
            >
              {current.type === "image" ? (
                <figure>
                  {/* eslint-disable-next-line @next/next/no-img-element -- served from the API host */}
                  <img src={current.src} alt={current.caption ?? event.title} className="aspect-[400/250] w-full rounded-xl border border-line bg-white object-cover" />
                  {current.caption && <figcaption className="mt-2 text-center text-sm text-ink-soft">{current.caption}</figcaption>}
                </figure>
              ) : (
                <EventVideo key={current.src} src={current.src} title={event.title} caption={current.caption} />
              )}
              {slides.length > 1 && (
                <>
                  <button type="button" aria-label="Previous" onClick={() => go(slide - 1)} className="absolute left-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition hover:bg-brand-600">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button type="button" aria-label="Next" onClick={() => go(slide + 1)} className="absolute right-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition hover:bg-brand-600">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="mt-3 flex justify-center gap-2">
                    {slides.map((k, n) => (
                      <button key={`${k.type}-${n}`} type="button" aria-label={k.type === "image" ? "Show image" : "Show video"} aria-current={n === slide} onClick={() => setSlide(n)} className={`h-2 rounded-full transition-all ${n === slide ? "w-6 bg-brand-600" : "w-2 bg-ink/25"}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {body.length > 0 && (
            <div className="space-y-3 text-[15px] leading-relaxed text-ink-soft">
              {body.map((p, i) => (
                <p key={i} className="whitespace-pre-line">
                  {p}
                </p>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
